import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { updateBrandKit, resetBrandKit } from '../../../features/brand-kit/brandKitSlice';
import { useUpdateBrandKitMutation } from '../../../features/brand-kit/brandKitApi';

interface CreateNewBrandKitModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  id?: string;
}

function CloseIcon() {
  return (
    <div className="relative shrink-0 size-[24px]" data-name="Frame">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
        <g id="Frame">
          <path d="M18 6L6 18" id="Vector" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M6 6L18 18" id="Vector_2" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </g>
      </svg>
    </div>
  );
}

export const CreateNewBrandKitModal: React.FC<CreateNewBrandKitModalProps> = ({ isOpen, onClose, mode = 'create', id }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const storedBrandKit = useAppSelector((state) => state.brandKit);
  const [updateBrandKitMutation, { isLoading: isUpdating }] = useUpdateBrandKitMutation();

  const [brandName, setBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'create') {
        dispatch(resetBrandKit());
        setBrandName('');
        setDescription('');
        setCategory('');
      } else {
        setBrandName(storedBrandKit.name || '');
        setDescription(storedBrandKit.description || '');
        setCategory(storedBrandKit.category || '');
      }
    }
  }, [isOpen, mode, dispatch, storedBrandKit.name, storedBrandKit.description, storedBrandKit.category]);


  useEffect(() => {
    if (brandName.trim().length > 0) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [brandName]);

  const handleAction = async () => {
    if (isValid && !isUpdating) {
      const updatedMetadata = { name: brandName, description, category };
      dispatch(updateBrandKit(updatedMetadata));

      if (mode === 'edit' && id) {
        try {
          await updateBrandKitMutation({
            id,
            data: { ...storedBrandKit, ...updatedMetadata }
          }).unwrap();
        } catch (error) {
          console.error('Failed to update brand kit metadata:', error);
        }
      } else if (mode === 'create') {
        navigate('/brand-kit/create/logo');
      }
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-[2.5px] bg-[rgba(16,25,40,0.5)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="fixed inset-0 z-40"
            onClick={onClose}
          />
          <motion.div
            className="bg-white relative overflow-clip rounded-[12px] w-[675px] shadow-lg flex flex-col z-50"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >

            {/* Header */}
            <div className="flex items-center justify-between w-full px-[18px] pt-[24px]">
              <div className="content-stretch flex flex-col gap-[8px] items-start leading-[0] relative shrink-0 w-[537px]">
                <div className="flex flex-col font-sans font-bold justify-center relative shrink-0 text-[#101928] text-[20px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  <p className="leading-[normal]">{mode === 'create' ? 'Create New Brand Kit' : 'Update Brand Kit'}</p>
                </div>
                <div className="flex flex-col font-sans font-normal justify-center relative shrink-0 text-[#475367] text-[14px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  <p className="leading-[normal]">Set up a new brand kit with your custom colors, typography, and design elements.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="cursor-pointer hover:opacity-70"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Inputs */}
            <div className="flex flex-col gap-[20px] items-start w-full px-[18px] mt-[26px]">

              {/* Brand Kit Name */}
              <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                <div className="flex flex-col font-sans font-normal justify-center leading-[0] relative shrink-0 text-[#101928] text-[13px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  <p className="leading-[normal]">
                    Brand Kit Name<span className="text-[#f04438]">*</span>
                  </p>
                </div>
                <div className="h-[36px] relative rounded-[4px] shrink-0 w-full">
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    className="absolute inset-0 w-full h-full rounded-[4px] border border-[#e4e7ec] px-3 py-2 text-sm focus:outline-none focus:border-[#30A84B]"
                    placeholder=""
                  />
                </div>
              </div>

              {/* Description */}
              <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                <div className="flex flex-col font-sans font-normal justify-center leading-[0] relative shrink-0 text-[#101928] text-[13px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  <p className="leading-[normal]">Description</p>
                </div>
                <div className="h-[93px] relative rounded-[4px] shrink-0 w-full">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="absolute inset-0 w-full h-full rounded-[4px] border border-[#e4e7ec] px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#30A84B]"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                <div className="flex flex-col font-sans font-normal justify-center leading-[0] relative shrink-0 text-[#101928] text-[13px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  <p className="leading-[normal]">Category</p>
                </div>
                <div className="h-[36px] relative rounded-[4px] shrink-0 w-full">
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="absolute inset-0 w-full h-full rounded-[4px] border border-[#e4e7ec] px-3 py-2 text-sm focus:outline-none focus:border-[#30A84B]"
                  />
                </div>
              </div>

            </div>

            {/* Actions */}
            <div className="flex gap-[16px] items-center justify-end w-full px-[18px] pb-[24px] mt-[36px]">
              {/* Cancel */}
              <button
                onClick={onClose}
                className="content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 border border-[#d0d5dd] hover:bg-gray-50 transition-colors"
              >
                <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[#101928] text-[14px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  Cancel
                </p>
              </button>

              {/* Continue / Update */}
              <button
                disabled={!isValid}
                onClick={handleAction}
                className={`content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 w-[94px] transition-colors ${isValid ? 'bg-[#30A84B] hover:bg-[#288c3e] cursor-pointer' : 'bg-[#bfe4c7] cursor-not-allowed'
                  }`}
              >
                <p className="font-sans font-semibold leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  {mode === 'create' ? 'Continue' : 'Update'}
                </p>
              </button>
            </div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

